import React, { useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { addDays, endOfMonth, format, parseISO } from 'date-fns';
import { inr, pct } from '@/components/po-grn/format';

export const ORANGE = '#ea580c';
export const BROWN = '#3b2416';
const short = (v) => (Math.abs(v) >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : Math.abs(v) >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : v);
const dm = (iso) => (iso?.length === 7 ? format(parseISO(`${iso}-01`), 'MMM yy') : iso ? format(parseISO(iso), 'dd-MM') : '');

export const ChartCard = ({ title, subtitle, children, testId, className = '', right, h = 'h-64', height }) => (
  <div className={`rounded-xl border border-orange-200/70 bg-white p-4 ${className}`} data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-sm font-semibold text-[#3b2416]">{title}</div>
        {subtitle && <div className="text-[11px] text-stone-500 mb-2">{subtitle}</div>}
      </div>
      {right}
    </div>
    <div className={height ? '' : h} style={height ? { height } : undefined}>{children}</div>
  </div>
);

const TwoLineTick = ({ x, y, payload, names }) => {
  const name = names[payload.value] || '';
  return (
    <text x={x} y={y} textAnchor="end" fontSize={10} fill="#44403c">
      <tspan x={x} dy={-2} fontWeight={600}>{payload.value}</tspan>
      <tspan x={x} dy={11}>{name.length > 30 ? `${name.slice(0, 29)}…` : name}</tspan>
    </text>
  );
};

const wrap2 = (text, max = 22) => {
  const words = String(text ?? '').split(/\s+/);
  let a = '';
  let i = 0;
  while (i < words.length && (a + ' ' + words[i]).trim().length <= max) { a = `${a} ${words[i]}`.trim(); i += 1; }
  if (!a) { a = words[0].slice(0, max); words[0] = words[0].slice(max); } else words.splice(0, i);
  const b = words.join(' ').trim();
  return [a, b.length > max ? `${b.slice(0, max - 1)}…` : b];
};

const WrapTick = ({ x, y, payload }) => {
  const [a, b] = wrap2(payload.value);
  return (
    <text x={x} y={y} textAnchor="end" fontSize={10} fill="#44403c">
      <tspan x={x} dy={b ? -2 : 3}>{a}</tspan>
      {b && <tspan x={x} dy={11}>{b}</tspan>}
    </text>
  );
};

export const BarList = ({ data, valueKey = 'sales', title, subtitle, testId, onPick, className, money = true, twoLine = false, h }) => {
  const names = Object.fromEntries((data || []).map((d) => [d.key, d.name]));
  return (
    <ChartCard title={title} subtitle={subtitle} testId={testId} className={className} h={h || (twoLine ? 'h-[35rem]' : 'h-64')}
      height={twoLine || h ? undefined : Math.max(256, (data || []).length * 30 + 40)}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 4, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" horizontal={false} />
          <XAxis type="number" fontSize={10} tickFormatter={short} />
          {twoLine ? (
            <YAxis type="category" dataKey="key" width={190} interval={0} tick={<TwoLineTick names={names} />} />
          ) : (
            <YAxis type="category" dataKey="name" width={150} interval={0} tick={<WrapTick />} />
          )}
          <Tooltip formatter={(v) => (money ? inr(v) : v)} labelFormatter={(l) => (twoLine ? `${l} · ${names[l] || ''}` : l)} />
          <Bar dataKey={valueKey} fill={ORANGE} radius={[0, 3, 3, 0]} cursor={onPick ? 'pointer' : undefined}
            onClick={(d) => onPick && onPick(d?.payload)}>
            {(data || []).map((d) => <Cell key={d.key ?? d.name} fill={(d[valueKey] || 0) < 0 ? '#dc2626' : ORANGE} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const periodRange = (gran, period) => {
  if (gran === 'monthly') { const s = parseISO(`${period}-01`); return [format(s, 'yyyy-MM-dd'), format(endOfMonth(s), 'yyyy-MM-dd')]; }
  if (gran === 'weekly') return [period, format(addDays(parseISO(period), 6), 'yyyy-MM-dd')];
  return [period, period];
};

export const SalesTrend = ({ trends, onDrill }) => {
  const [gran, setGran] = useState('monthly');
  const data = trends?.[gran] || [];
  return (
    <ChartCard title="Sales Trend (invoices + DCs)" subtitle="Click a bar to drill into transactions for that period" testId="sa-chart-trend" className="lg:col-span-2"
      right={(
        <div className="flex rounded-md border border-orange-200 p-0.5 text-[11px]">
          {['daily', 'weekly', 'monthly'].map((g) => (
            <button key={g} type="button" onClick={() => setGran(g)} data-testid={`sa-trend-${g}`}
              className={`px-2 py-0.5 rounded capitalize ${gran === g ? 'bg-[#3b2416] text-white' : 'text-stone-600'}`}>{g}</button>
          ))}
        </div>
      )}>
      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" vertical={false} />
          <XAxis dataKey="period" fontSize={10} tickFormatter={dm} />
          <YAxis fontSize={10} tickFormatter={short} />
          <Tooltip formatter={(v) => inr(v)} labelFormatter={dm} />
          <Bar dataKey="sales" name="Sales" fill={ORANGE} radius={[3, 3, 0, 0]} cursor="pointer"
            onClick={(d) => { const [a, b] = periodRange(gran, d?.payload?.period); onDrill({ patch: { date_from: a, date_to: b, date_preset: 'custom', flag: ['sales'] } }, `Sales ${dm(d?.payload?.period)}`); }} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const SalesVsMargin = ({ monthly, onDrill }) => (
  <ChartCard title="Monthly Sales vs Current Margin" subtitle="Current Margin % = Σ (SP − Inward) × Qty ÷ Σ (SP × Qty) × 100" testId="sa-chart-sales-margin">
    <ResponsiveContainer>
      <ComposedChart data={monthly || []}>
        <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" vertical={false} />
        <XAxis dataKey="period" fontSize={10} tickFormatter={dm} />
        <YAxis yAxisId="v" fontSize={10} tickFormatter={short} />
        <YAxis yAxisId="p" orientation="right" fontSize={10} unit="%" />
        <Tooltip formatter={(v, n) => (n === 'Current Margin %' ? pct(v) : inr(v))} labelFormatter={dm} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar yAxisId="v" dataKey="sales" name="Sales" fill="#fdba74" radius={[3, 3, 0, 0]} cursor="pointer"
          onClick={(d) => onDrill({ patch: { month: d?.payload?.period, flag: ['sales'] } }, `Month ${dm(d?.payload?.period)}`)} />
        <Bar yAxisId="v" dataKey="margin" name="Current Margin" fill={BROWN} radius={[3, 3, 0, 0]} />
        <Line yAxisId="p" dataKey="margin_pct" name="Current Margin %" stroke={ORANGE} strokeWidth={2} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  </ChartCard>
);

export const MarginDistribution = ({ data, onDrill }) => (
  <ChartCard title="Current Margin % Distribution" subtitle="Lines by (SP − Inward) ÷ SP × 100, confirmed pricing only" testId="sa-chart-margin-dist">
    <ResponsiveContainer>
      <BarChart data={data || []}>
        <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" vertical={false} />
        <XAxis dataKey="bucket" fontSize={10} />
        <YAxis fontSize={10} allowDecimals={false} />
        <Tooltip formatter={(v, n) => (n === 'Sales' ? inr(v) : v)} />
        <Bar dataKey="lines" name="Lines" radius={[3, 3, 0, 0]} cursor="pointer"
          onClick={(d) => d?.payload?.bucket === '< 0%' && onDrill({ patch: { flag: ['sales', 'negative_margin'] } }, 'Negative margin lines')}>
          {(data || []).map((d) => <Cell key={d.bucket} fill={d.bucket === '< 0%' ? '#dc2626' : ORANGE} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </ChartCard>
);

export const NegativeByMonth = ({ data, onDrill }) => (
  <ChartCard title="Negative-Margin Transactions" subtitle="Count and value by month · click to drill" testId="sa-chart-negative">
    <ResponsiveContainer>
      <ComposedChart data={data || []}>
        <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" vertical={false} />
        <XAxis dataKey="period" fontSize={10} tickFormatter={dm} />
        <YAxis yAxisId="c" fontSize={10} allowDecimals={false} />
        <YAxis yAxisId="v" orientation="right" fontSize={10} tickFormatter={short} />
        <Tooltip formatter={(v, n) => (n === 'Value' ? inr(v) : v)} labelFormatter={dm} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar yAxisId="c" dataKey="count" name="Lines" fill="#dc2626" radius={[3, 3, 0, 0]} cursor="pointer"
          onClick={(d) => onDrill({ patch: { month: d?.payload?.period, flag: ['sales', 'negative_margin'] } }, `Negative ${dm(d?.payload?.period)}`)} />
        <Line yAxisId="v" dataKey="value" name="Value" stroke={BROWN} strokeWidth={2} />
      </ComposedChart>
    </ResponsiveContainer>
  </ChartCard>
);
